import React from 'react';
import styled, { css } from 'styled-components';

interface SectionProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
  backgroundColor?: 'primary' | 'secondary' | 'accent' | 'light' | 'dark' | 'background' | 'darkBg';
  fullWidth?: boolean;
  paddingTop?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'none';
  paddingBottom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'none';
}

const getBackgroundColor = (color: string, theme: any) => {
  const colors = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    light: theme.colors.light,
    dark: theme.colors.dark,
    background: theme.colors.background,
    darkBg: theme.colors.darkBg,
  };

  return colors[color as keyof typeof colors] || theme.colors.background;
};

const getTextColor = (backgroundColor: string, theme: any) => {
  const darkBackgrounds = ['primary', 'secondary', 'accent', 'dark', 'darkBg'];
  return darkBackgrounds.includes(backgroundColor) ? theme.colors.light : theme.colors.dark;
};

const getPadding = (size: string, theme: any) => {
  if (size === 'none') return '0';

  const paddings = {
    xs: theme.spacing.xs,
    sm: theme.spacing.sm,
    md: theme.spacing.md,
    lg: theme.spacing.lg,
    xl: theme.spacing.xl,
    xxl: theme.spacing.xxl,
    xxxl: theme.spacing.xxxl,
  };

  return paddings[size as keyof typeof paddings] || theme.spacing.xxl;
};

const StyledSection = styled.section<SectionProps>`
  background-color: ${props => getBackgroundColor(props.backgroundColor || 'background', props.theme)};
  color: ${props => getTextColor(props.backgroundColor || 'background', props.theme)};
  padding-top: ${props => getPadding(props.paddingTop || 'xxl', props.theme)};
  padding-bottom: ${props => getPadding(props.paddingBottom || 'xxl', props.theme)};
  width: 100%;
  
  ${props => props.fullWidth && css`
    .container {
      max-width: 100%;
      padding: 0;
    }
  `}
`;

const SectionContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const Section: React.FC<SectionProps> = ({
  children,
  id,
  className,
  backgroundColor = 'background',
  fullWidth = false,
  paddingTop = 'xxl',
  paddingBottom = 'xxl',
}) => {
  return (
    <StyledSection
      id={id}
      className={className}
      backgroundColor={backgroundColor}
      fullWidth={fullWidth}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >
      <SectionContent className="container">
        {children}
      </SectionContent>
    </StyledSection>
  );
};

export default Section; 